export { formatCurrency, formatMinor } from '@repletoryreach/sdk-utils/format';

export function formatInteger(n: number, locale = 'en-ZA'): string {
  return new Intl.NumberFormat(locale).format(n);
}

export function formatDateTime(iso: string, locale = 'en-ZA'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDate(iso: string, locale = 'en-ZA'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { dateStyle: 'medium' });
}

export function formatRelative(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const seconds = Math.round((t - now) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  return rtf.format(Math.round(seconds / 86400), 'day');
}

export function shortId(id: string, len = 8): string {
  return id.length <= len ? id : `${id.slice(0, len)}…`;
}

/**
 * Bytes at human scale. Binary units, because every producer of these figures
 * (RabbitMQ queue memory, object storage) counts in powers of two.
 */
export function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

/**
 * A per-second rate. Small rates keep a decimal, because 0,4 messages a second
 * and 0 are different states; a busy queue rounds to whole messages.
 */
export function formatRate(n: number, locale = 'en-ZA'): string {
  if (!Number.isFinite(n)) return '0';
  if (n === 0) return '0';
  if (n < 10) return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n);
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
}

/**
 * A duration given in milliseconds, at the coarsest unit that stays honest.
 * Used for the queue message TTL, which the broker reports in milliseconds and
 * operators set in minutes or hours.
 */
export function humanDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 90) return `${Number(seconds.toFixed(seconds < 10 ? 1 : 0))} s`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 48) return `${Number(hours.toFixed(hours < 10 ? 1 : 0))} h`;
  return `${Math.round(hours / 24)} days`;
}
